import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, timer } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AuthFacadeService } from './core/auth-facade.service';
import { AnalyticsService } from './core/analytics.service';
import { ExtensionBridgeService } from './core/extension-bridge.service';
import { initSiteAnalytics } from './core/site-analytics';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'advocad-app';

  private _auth = inject(AuthFacadeService);
  private _router = inject(Router);
  private _analytics = inject(AnalyticsService);
  // Constructed at app root so it starts broadcasting auth identity to the
  // capture extension immediately — not only when the assisted-capture
  // component happens to mount during a gated crawl.
  private _extBridge = inject(ExtensionBridgeService);

  // Is this page load an Auth0 redirect callback? (Auth0 appends ?code=&state=
  // before the SDK strips them.) Captured at construction, before that happens.
  private readonly _isCallback =
    /[?&]code=/.test(window.location.search) &&
    /[?&]state=/.test(window.location.search);

  /**
   * Whether to show the branded splash. Shows while the SDK is loading, and on
   * a login callback holds for a minimum of 3s so the animation doesn't just
   * flash by. Normal refreshes get no minimum (snappy).
   */
  readonly showSplash$ = combineLatest([
    this._auth.isLoading$,
    timer(this._isCallback ? 3000 : 0).pipe(
      map(() => true),
      startWith(false),
    ),
  ]).pipe(map(([loading, minElapsed]) => loading || !minElapsed));

  ngOnInit(): void {
    // Analytics: PostHog (product) + GA4 (Firebase), both live-hosts only.
    this._analytics.init();
    initSiteAnalytics();
    this._auth.userId$.subscribe((sub) => {
      if (sub) this._analytics.identify(sub);
    });

    // A blocked/failed redirect login bounces back to the app root. We're the
    // single long-lived subscriber, so we consume the error once here, stash a
    // one-shot flag on the facade, and route to /login — which renders the
    // "verify your email" view (or an inline error for generic failures).
    this._auth.loginError$.subscribe((e) => {
      if (!e) return;
      if (e.needsVerification) {
        this._auth.flagUnverified(this._auth.pendingLoginEmail());
      } else {
        this._auth.flagAuthError(e.description);
      }
      void this._router.navigate(['/login']);
    });
  }
}
